import { FC, useState } from 'react'

import {
  Button,
  ButtonColors,
  ButtonSizes,
  ButtonVariants
} from '@inno-ui/button'
import {
  Illustrations,
  Modal,
  ModalVariants,
  TitleColors
} from '@inno-ui/modal'
import { useTranslation } from 'react-i18next'

import styles from '../ReviewQueue.module.scss'

type Decision = 'approve' | 'decline'

interface DecisionFormProps {
  // 'approve-decline' = the pending path (Approve + Decline side by side).
  // 'revoke' = the approved path (single Revoke button, no Approve).
  variant: 'approve-decline' | 'revoke'
  // disabled while a mutation is in-flight
  busy: boolean
  onSubmit: (decision: Decision, reason?: string) => void
}

// Reason textarea + action buttons. Two visual variants — approve/decline
// shows the full pair, revoke collapses to the single destructive button.
// Reason text is owned here and shipped up via onSubmit.
//
// Approve fires immediately (non-destructive). Decline / Revoke open a confirm
// modal first so a misclick doesn't take down a live widget or reject a
// pending one.
export const DecisionForm: FC<DecisionFormProps> = ({
  variant,
  busy,
  onSubmit
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'widgets.aiAgent' })
  const [reason, setReason] = useState('')
  const [confirming, setConfirming] = useState(false)
  const isRevoke = variant === 'revoke'

  const submit = (decision: Decision) =>
    onSubmit(decision, reason.trim() || undefined)

  const destructiveLabel = isRevoke ? t('revoke') : t('decline')
  const confirmTitle = isRevoke
    ? t('confirm_revoke_title')
    : t('confirm_decline_title')
  const confirmBody = isRevoke
    ? t('confirm_revoke_body')
    : t('confirm_decline_body')

  return (
    <div className={isRevoke ? styles.revokeBar : styles.decision}>
      {/* Approved widgets are only here to be revoked — no standing note box.
          The optional reason is collected in the confirm step instead. Pending
          widgets keep the note so a decline carries feedback to the
          creator. */}
      {!isRevoke && (
        <>
          <span className={styles.sectionTitle}>{t('reason_label')}</span>
          <textarea
            className={styles.reason}
            value={reason}
            placeholder={t('decline_reason_placeholder')}
            onChange={e => setReason(e.target.value)}
            rows={3}
          />
        </>
      )}
      <div className={styles.actions}>
        {isRevoke ? (
          <Button
            variant={ButtonVariants.Ghost}
            color={ButtonColors.Red}
            size={ButtonSizes.M}
            label={destructiveLabel}
            disabled={busy}
            onClick={() => setConfirming(true)}
          />
        ) : (
          <>
            <Button
              variant={ButtonVariants.Dark}
              size={ButtonSizes.M}
              label={t('approve')}
              disabled={busy}
              onClick={() => submit('approve')}
            />
            <Button
              variant={ButtonVariants.Ghost}
              color={ButtonColors.Red}
              size={ButtonSizes.M}
              label={destructiveLabel}
              disabled={busy}
              onClick={() => setConfirming(true)}
            />
          </>
        )}
      </div>

      {confirming && (
        <Modal
          isOpen
          variant={ModalVariants.ActionConfirmation}
          zIndex={9999}
          title={confirmTitle}
          description={confirmBody}
          titleColor={TitleColors.Red}
          illustration={
            isRevoke ? Illustrations.Trash : Illustrations.QuestionMark
          }
          isLoading={busy}
          onCancel={() => setConfirming(false)}
          onClose={() => setConfirming(false)}
          buttons={[
            {
              label: t('cancel'),
              onClick: () => setConfirming(false)
            },
            {
              label: destructiveLabel,
              color: ButtonColors.Red,
              disabled: busy,
              onClick: () => {
                setConfirming(false)
                submit('decline')
              }
            }
          ]}
        />
      )}
    </div>
  )
}
